import { useState, useEffect, useCallback } from "react";

/**
 * Custom hook for client-side pagination.
 *
 * @param {Function} fetchDataFn - Async function that returns the complete data array.
 * @param {Object} options - Configuration options.
 * @param {number} options.itemsPerPage - Number of items to show per page (default: 12).
 * @param {Array} options.dependencies - Dependency array for the useEffect hook (default: []).
 */
export const usePagination = (
  fetchDataFn,
  { itemsPerPage = 10, dependencies = [] } = {},
) => {
  const [data, setData] = useState([]);
  const [displayedData, setDisplayedData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(itemsPerPage);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchDataFn();
      // Ensure result is an array
      const dataArray = Array.isArray(result) ? result : [];
      setData(dataArray);

      // Calculate total pages
      const total = Math.ceil(dataArray.length / currentItemsPerPage);
      setTotalPages(total || 1);

      // Reset to page 1 on new data fetch
      setPage(1);
      setDisplayedData(dataArray.slice(0, currentItemsPerPage));
    } catch (err) {
      console.error("Pagination Error:", err);
      setError(err.message || "Failed to load data");
      setData([]);
      setDisplayedData([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [fetchDataFn, currentItemsPerPage]);

  useEffect(() => {
    loadData();
  }, [...dependencies, loadData]);

  // Handle page change
  const goToPage = useCallback(
    (pageNumber) => {
      if (pageNumber < 1 || pageNumber > totalPages) return;
      setPage(pageNumber);
      const start = (pageNumber - 1) * currentItemsPerPage;
      const end = start + currentItemsPerPage;
      setDisplayedData(data.slice(start, end));
    },
    [data, totalPages, currentItemsPerPage],
  );

  const changeItemsPerPage = useCallback(
    (newSize) => {
      setCurrentItemsPerPage(newSize);
      const total = Math.ceil(data.length / newSize);
      setTotalPages(total || 1);

      const newPage = page > total ? total : page;
      setPage(newPage || 1);

      const start = ((newPage || 1) - 1) * newSize;
      const end = start + newSize;
      setDisplayedData(data.slice(start, end));
    },
    [data, page],
  );

  const refresh = useCallback(() => {
    loadData();
  }, [loadData]);

  // Helper to manually update local state (e.g. after delete/edit) usage
  const updateLocalData = useCallback(
    (newData) => {
      setData(newData);
      const total = Math.ceil(newData.length / currentItemsPerPage);
      setTotalPages(total || 1);

      // Adjust page if current page exceeds new total pages
      const newPage = page > total ? total : page;
      setPage(newPage || 1);

      const start = (newPage - 1) * currentItemsPerPage;
      const end = start + currentItemsPerPage;
      setDisplayedData(newData.slice(start, end));
    },
    [page, currentItemsPerPage],
  );

  return {
    data, // The full dataset
    displayedData, // The currently visible subset
    loading,
    error,
    page,
    totalPages,
    goToPage,
    refresh,
    updateLocalData,
    itemsPerPage: currentItemsPerPage,
    changeItemsPerPage,
    totalItems: data.length,
  };
};
